import React, {useState, useEffect, useMemo, useRef} from 'react';
import { useHistory, useLocation } from 'react-router';
import Tooltip from '@mui/material/Tooltip';
import dayjs from 'dayjs';
import moment from 'moment';
import styles from './CustomOfferDetailsPage.module.scss';
import {TopBarRedesigned} from "../../../components/TopBarRedesigned";
import { formatPartialOfferTime, formatPartialOfferTimeRange, debugTimeFormatting } from '../../../utils/timeFormatting';
import {
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Event as EventIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Info as InfoIcon,
    Work as WorkIcon,
    Schedule as ScheduleIcon,
    CalendarToday as CalendarTodayIcon,
    Description as DescriptionIcon,
    WorkOutline as WorkOutlineIcon,
    ThumbUpAltOutlined as ThumbUpAltOutlinedIcon,
    Autorenew as AutorenewIcon
} from '@mui/icons-material';
import Api from 'api'
import {BasicHygienistScoreProps, HYGIENIST_TYPES, HygienistScoreProps, OfferHygienist, PartialOffer, PositionProps, SkillsProps, Type, UserAccountProps} from "utils/types";
import { PARTIAL_OFFER_STATUS } from "utils/constants";
import { formatProfileHygienistName} from "utils";
import { formatFirstLicensedLabel } from "utils/educationCredentials";
import { getPerformanceScoreDisplay } from "utils/performanceScoreDisplay";
import { getOfficeHistoryCopy, useOfficeHistory } from 'utils/officeHistory';
import {
  formatShiftLocationLines,
  resolveInstabookHourlyRate,
} from 'utils/dentist/instabookOfferDisplay';
import AcceptOffer from './components/AcceptOffer';
import DeclineOffer from './components/DeclineOffer'
import BookAnotherTemp from './components/BookAnotherTemp';
import CancelShiftButton from './components/CancelShift';
import {StyledButton} from "../../../components/Buttons/DefaultButton/DefaultButton.styles";
import FixedLoader from "../../../components/FixedLoader";
import ProfileImage from './components/ProfileImage';
import RequestResubmitModal from './components/RequestResubmit';
import _ from "lodash";
import TalentResume from "../../../components/TalentResume";
import Popup from "../../../components/Popup";
import useActionApi from "../../../hooks/useActionApi";
import TimeDebugger from "../../../components/TimeDebugger/TimeDebugger";
import { Language } from 'api/Api';
import { Analytics, getOfficeAnalyticsContext } from 'utils/analytics';
import { getRevenueCustomerId, hasOfficeRevenuePaymentInfo, PaymentPurpose, withFallbackPaymentCountry } from 'utils/paymentPurpose';
import { PaymentPromptCheckpoint, PaymentPromptSourcePage } from 'utils/paymentPromptAnalytics';
import {
  buildOfferDetailsPaymentInfoRoute,
  getOfferDetailsPaymentBlock,
  loadProtectedOfferDetails,
  OFFER_DETAILS_PAYMENT_UNAVAILABLE_CODE,
} from 'utils/offerDetailsPayment';
import PaymentRequiredModal from '../DentistHomePage/components/PaymentRequiredModal/PaymentRequiredModal';

interface OfferData {
    partialOffer: PartialOffer | null;
    hygienist: OfferHygienist | null;
    workExperience: PositionProps[];
    isFavouriteHygienist: boolean;
    job: any
}

const createEmptyOfferData = (): OfferData => ({
  partialOffer: null,
  hygienist: null,
  workExperience: [],
  isFavouriteHygienist: false,
  job: {},
});

interface LocationState {
  offerId: number;
  offerDetailsSourcePage?: PaymentPromptSourcePage;
}
declare let window: any
declare let cordova: any

const RELIABILITY_TOOLTIP = "Talent Reliability Score: A measure of how dependably this talent shows up for shifts they've booked — based on their history of completed shifts versus cancellations (with last-minute cancellations weighing more heavily and recent behavior counting more than older activity).";
const FEEDBACK_TOOLTIP = "Overall Feedback Score: A rolling average of the star ratings this talent has received from dental offices across past shifts (covering clinical skills, professionalism, communication, time management, and rate satisfaction) — giving you an at-a-glance sense of how other offices have experienced working with them.";

export type InstabookBookNowDecision =
  | { action: 'block'; requiresCardSetup: false; isCardInvalid: false; locationId: number | null }
  | { action: 'prompt'; requiresCardSetup: boolean; isCardInvalid: boolean; locationId: number }
  | { action: 'book'; requiresCardSetup: false; isCardInvalid: false; locationId: number };

export const getInstabookBookNowDecision = (
  job: { locationId?: number | string } | null | undefined,
  user: UserAccountProps | null | undefined,
): InstabookBookNowDecision => {
  const locationId = Number(job?.locationId);
  if (!Number.isFinite(locationId) || locationId <= 0 || !user) {
    return { action: 'block', requiresCardSetup: false, isCardInvalid: false, locationId: null };
  }

  const location = user.locations?.find((candidate) => Number(candidate.id) === locationId);
  if (!location) {
    return { action: 'block', requiresCardSetup: false, isCardInvalid: false, locationId };
  }

  const paymentLocation = withFallbackPaymentCountry(
    location,
    user.dentist?.country ?? user.country,
  );
  if (!getRevenueCustomerId(paymentLocation)) {
    return { action: 'prompt', requiresCardSetup: true, isCardInvalid: false, locationId };
  }
  if (!hasOfficeRevenuePaymentInfo(paymentLocation)) {
    return { action: 'prompt', requiresCardSetup: false, isCardInvalid: true, locationId };
  }
  return { action: 'book', requiresCardSetup: false, isCardInvalid: false, locationId };
};

export const buildInstabookDetailsPaymentInfoRoute = (
  job: { id?: number | string; locationId?: number | string },
  locationId: number | null,
  checkpointOrigin: PaymentPromptCheckpoint = 'instabook_book_now',
) => ({
  pathname: 'paymentInfo',
  state: {
    locationId: locationId ?? job.locationId,
    nextPage: 'jobPosted',
    jobId: job.id,
    paymentPurpose: 'revenue',
    sourcePage: 'custom_offer_details',
    checkpointOrigin,
  },
});

export const getOfferDetailsPaymentDismissRoute = ({
  sourcePage,
  jobId,
}: {
  sourcePage: PaymentPromptSourcePage;
  jobId?: number | string | null;
}) => {
  const normalizedJobId = Number(jobId);
  const hasJobId = Number.isFinite(normalizedJobId) && normalizedJobId > 0;

  if (sourcePage === 'custom_offers' && hasJobId) {
    return { pathname: 'dentistCustomOffers', state: { id: normalizedJobId } };
  }
  if (sourcePage === 'job_posted' && hasJobId) {
    return { pathname: 'jobPosted', state: { id: normalizedJobId } };
  }
  return { pathname: 'dentistHome' };
};

const isMobileTooltip = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia?.('(hover: none), (pointer: coarse)').matches || window.innerWidth <= 768;
};

const getScoreValueClass = (label: string) => {
  switch (label) {
    case 'Outstanding':
    case 'Very Good':
    case 'Great':
      return styles.scoreValueGreen;
    case 'Good':
      return styles.scoreValueBlue;
    case 'Fair':
      return styles.scoreValueYellow;
    case 'Low':
      return styles.scoreValueCoral;
    default:
      return styles.scoreValueMuted;
  }
};

const formatTime = (timeStr: string | undefined): string => {
  if (!timeStr) return '';
  
  // Try parsing with different formats using dayjs
  const formats = ['YYYY-MM-DD HH:mm:ss', 'HH:mm:ss', 'HH:mm', 'h:mm a'];
  
  for (const format of formats) {
    const time = dayjs(timeStr, format);
    if (time.isValid()) {
      return time.format('h:mm A');
    }
  }
  
  // If no format matches, try default parsing
  const time = dayjs(timeStr);
  return time.isValid() ? time.format('h:mm A') : timeStr;
};

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, 
  icon, 
  children, 
  defaultOpen = true 
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className={styles.collapsibleSection}>
      <div 
        className={styles.sectionHeader} 
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className={styles.sectionTitle}>
          <span className={styles.sectionIcon}>{icon}</span>
          {title}
        </div>
        <div className={styles.chevronIcon}>
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </div>
      </div>
      {isOpen && <div className={styles.sectionContent}>{children}</div>}
    </div>
  );
};

const CustomOfferDetailsPage: React.FC = () => {
  const history = useHistory();
  const location = useLocation<LocationState & {
    mode?: 'instabook';
    job?: any;
    hygienistId?: number;
    instabookHourlyRate?: number;
  }>();

  const [offerId, setOfferId] = useState<number | null>(null);
  const instabookMode = location.state?.mode === 'instabook';
  const jobFromState = location.state?.job;
  const hygienistIdFromState = location.state?.hygienistId;
  const instabookHourlyRateFromState = location.state?.instabookHourlyRate;
  const offerDetailsSourcePage = location.state?.offerDetailsSourcePage || 'custom_offer_details';

  const [offerData, setOfferData] = useState<OfferData>(createEmptyOfferData);
  const officeHistory = useOfficeHistory(
    Number(instabookMode ? hygienistIdFromState : offerData.partialOffer?.hygienistId) || null,
    Number(instabookMode ? jobFromState?.locationId : offerData.partialOffer?.location?.id) || null,
  );

  const languages = offerData.hygienist?.languages ?? [];
  const languagesToShow = languages.filter((lang: Language) => lang.code !== 'en') ?? [];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [licensed, setLicensed] = useState<boolean>(false);
  const [isExpired, setIsExpired] = useState<boolean>(false);
  const [latestWorkExperience, setLatestWorkExperience] = useState<PositionProps>();
  const [isResumeModalOpen, setIsResumeModalOpen] = useState(false)
  const [apiSkills, setApiSkills] = useState<SkillsProps[]>([])
  const offerDetailsTrackedRef = useRef(false);
  const [officeUser, setOfficeUser] = useState<UserAccountProps | null>(null);
  const [officeUserLoading, setOfficeUserLoading] = useState(instabookMode);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentLocationId, setPaymentLocationId] = useState<number | null>(null);
  const [paymentJobId, setPaymentJobId] = useState<number | null>(null);
  const [requiresCardSetup, setRequiresCardSetup] = useState(false);
  const [isCardInvalid, setIsCardInvalid] = useState(false);
  const [paymentPolicy, setPaymentPolicy] = useState<'standard_revenue' | '123dentist_corporate' | null>(null);
  const [paymentPurpose, setPaymentPurpose] = useState<PaymentPurpose | null>(null);
  const [corporateManaged, setCorporateManaged] = useState(false);
  const [paymentUnavailable, setPaymentUnavailable] = useState(false);
  const [bookingInstabook, setBookingInstabook] = useState(false);

  const trackOfferDetailsViewed = (locationId?: number | string | null, mode = 'custom_offer') => {
    if (offerDetailsTrackedRef.current) return;
    offerDetailsTrackedRef.current = true;
    Analytics.track('offer details viewed', {
      ...getOfficeAnalyticsContext(locationId),
      source_page: 'custom_offer_details',
      offer_mode: mode,
    });
  };

  const [cancellationStats, setCancellationStats] = useState<BasicHygienistScoreProps>({
    cancellationScore: 0,
    overallReliability: 0,
    recentReliability: 0,
    weightedReliability: 0
});

  const goBack = () => {
    history.goBack();
  }

  const [, , getSkills] = useActionApi({
    apiMethod: Api.getSkills,
    onSuccess: (skills: SkillsProps[]) => {
      if(_.isEmpty(skills)) return
      setApiSkills(skills)
    },
  });

  const [statsLoading, , getStats] = useActionApi({
    apiMethod: Api.getCancellationStats,
    onSuccess: (data: BasicHygienistScoreProps) => {
      setCancellationStats(data)
    },
  })

  const fetchData = async (id: number) => {
    setLoading(true);
    setError(null);
    setIsPaymentModalOpen(false);
    setOfferData(createEmptyOfferData());

    try {
      // The server protects both calls. Keep them sequential so a blocked
      // offer is never marked viewed before the payment decision succeeds.
      const partialOffer = await loadProtectedOfferDetails({
        offerId: id,
        getOffer: Api.getPartialOffer,
        updateViews: Api.updatePartialOfferViews,
      });

      if (!partialOffer || !partialOffer.hygienistId) {
        throw new Error("Invalid offer data");
      }
      
      // Store the hygienist data from partialOffer (which includes showForHire)
      const hygienistFromOffer = partialOffer.hygienist;
      
      // Use the job data that comes with the partial offer (which has originalPostedStart/End)
      // instead of making a separate API call that would overwrite it
      
      let [hygienistResume] = await Promise.all([
        Api.getResumeData({
          hygienistId: partialOffer.hygienistId,
          locationId: partialOffer.location.id,
          offerId: partialOffer.id,
        }),
        getSkills({type: partialOffer.job?.type, state: partialOffer.location.province}),
        getStats({hygienistId: partialOffer.hygienistId})
      ]);

      // Merge hygienist data, preserving showForHire from partialOffer if it exists
      if (hygienistFromOffer && hygienistFromOffer.showForHire !== undefined) {
        hygienistResume = {
          ...hygienistResume,
          showForHire: hygienistFromOffer.showForHire
        };
      }
      if (hygienistResume?.type === HYGIENIST_TYPES.HYGIENIST && hygienistResume?.CDHONumber) {
        setLicensed(true);
      }

      setOfferData({
        partialOffer,
        hygienist: hygienistResume,
        workExperience: hygienistResume?.workExperience,
        isFavouriteHygienist: hygienistResume?.isFavouriteHygienist,
        job: partialOffer.job // Use job data from partial offer response
      })
      setPaymentUnavailable(false);
      setCorporateManaged(false);
      trackOfferDetailsViewed(partialOffer.location?.id);
      
    } catch (err: unknown) {
      const paymentBlock = getOfferDetailsPaymentBlock(err);
      if (paymentBlock) {
        setPaymentLocationId(paymentBlock.locationId);
        setPaymentJobId(paymentBlock.jobId);
        setRequiresCardSetup(paymentBlock.requiresCardSetup);
        setIsCardInvalid(paymentBlock.isCardInvalid);
        setPaymentPolicy(paymentBlock.paymentPolicy);
        setPaymentPurpose(paymentBlock.paymentPurpose);
        setCorporateManaged(paymentBlock.corporateManaged);
        setPaymentUnavailable(paymentBlock.code === OFFER_DETAILS_PAYMENT_UNAVAILABLE_CODE);
        setIsPaymentModalOpen(true);
        return;
      }
      console.error("Error fetching offer data:", err);
      setError("Failed to fetch offer details");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (instabookMode) {
      // Build a minimal dataset to render details UI without offer
      (async () => {
        try {
          await Api.updateToken();
          let hyg: any = null;
          try {
            hyg = hygienistIdFromState ? await Api.getHygienist(hygienistIdFromState) : null;
          } catch (e) {
            // 401 likely; proceed without hyg profile
          }
          let resume: any = null;
          try {
            resume = hygienistIdFromState && jobFromState?.locationId
              ? await Api.getResumeData({
                  hygienistId: hygienistIdFromState,
                  locationId: jobFromState.locationId,
                  jobId: jobFromState.id,
                })
              : null;
          } catch (e) {
            // ignore resume errors
          }
          setOfferData({
            partialOffer: null,
            hygienist: resume || hyg || null,
            workExperience: resume?.workExperience || [],
            isFavouriteHygienist: (resume?.isFavouriteHygienist || hyg?.isFavouriteHygienist) ?? false,
            job: jobFromState || {}
          });
          setLoading(false);
        } catch (e) {
          setLoading(false);
        }
      })();
    } else {
      const currentOfferId = (location.state as any)?.offerId;
      if (currentOfferId && currentOfferId !== offerId) {
        setOfferId(currentOfferId);
        fetchData(currentOfferId);
      }
    }
  }, [location.state, offerId, instabookMode, hygienistIdFromState, jobFromState]);

  useEffect(() => {
    if (!instabookMode) {
      setOfficeUserLoading(false);
      return;
    }

    let active = true;
    setOfficeUserLoading(true);
    Api.getUser()
      .then((user) => {
        if (active) setOfficeUser(user);
      })
      .catch(() => {
        if (active) setOfficeUser(null);
      })
      .finally(() => {
        if (active) setOfficeUserLoading(false);
      });
    return () => {
      active = false;
    };
  }, [instabookMode]);

  const handleInstabookBookNow = async () => {
    if (officeUserLoading || bookingInstabook) return;

    const decision = getInstabookBookNowDecision(jobFromState, officeUser);
    if (decision.action === 'block') {
      setError('Unable to verify payment information. Please refresh and try again.');
      return;
    }
    if (decision.action === 'prompt') {
      setPaymentLocationId(decision.locationId);
      setRequiresCardSetup(decision.requiresCardSetup);
      setIsCardInvalid(decision.isCardInvalid);
      setIsPaymentModalOpen(true);
      return;
    }

    if (!jobFromState?.id || !hygienistIdFromState) return;
    setBookingInstabook(true);
    setError(null);
    try {
      await Api.updateToken();
      await Api.instabookHygienist({ jobId: jobFromState.id, hygienistId: hygienistIdFromState });
      history.goBack();
    } catch (e) {
      setError('Unable to submit the Insta-Book request. Please try again.');
    } finally {
      setBookingInstabook(false);
    }
  };

  const handleGoPaymentInfo = (
    locationId: number | null,
    checkpointOrigin: PaymentPromptCheckpoint,
  ) => {
    setIsPaymentModalOpen(false);
    if (!instabookMode && offerId && locationId && paymentPurpose) {
      history.push(buildOfferDetailsPaymentInfoRoute({
        offerId,
        jobId: paymentJobId || (location.state as any)?.job?.id || (location.state as any)?.partialOffer?.jobId,
        locationId,
        paymentPurpose,
        sourcePage: offerDetailsSourcePage,
      }));
      return;
    }
    history.push(buildInstabookDetailsPaymentInfoRoute(jobFromState || {}, locationId, checkpointOrigin));
  };

  const retryPaymentCheck = () => {
    setIsPaymentModalOpen(false);
    if (offerId) fetchData(offerId);
  };

  const handleClosePaymentModal = () => {
    setIsPaymentModalOpen(false);
    if (instabookMode) return;

    history.replace(getOfferDetailsPaymentDismissRoute({
      sourcePage: offerDetailsSourcePage,
      jobId: paymentJobId
        || (location.state as any)?.job?.id
        || (location.state as any)?.partialOffer?.jobId,
    }));
  };

  const shiftDateDisplay = useMemo(() => {
    if (instabookMode) {
      const d = (offerData.job as any)?.shifts?.[0]?.shiftDate || (offerData.job as any)?.startDate;
      return d ? dayjs(d).format('dddd, MMMM D') : '';
    }
    return dayjs(offerData.partialOffer?.job?.startDate).format('dddd, MMMM D');
  }, [instabookMode, offerData.job, offerData.partialOffer]);

  useEffect(() => {
    if(!_.isEmpty(offerData)) {
      const now = new Date();
      const expiresAt = new Date(offerData.partialOffer?.expiresAt || '');
      const timeDiff = expiresAt.getTime() - now.getTime();
      // T2-5833: trust the row's `status` as the source of truth in addition
      // to the computed expiresAt window. The BE backfills `expiresAt` from
      // `createdOn + 18h`, so an offer that was administratively flipped to
      // EXPIRED (e.g. via the resubmit flow) still has a future expiresAt
      // and would otherwise render Accept / Decline instead of "Request
      // this offer to be resubmitted".
      const rawStatus: any = offerData.partialOffer?.status;
      const statusIsExpired =
        rawStatus === PARTIAL_OFFER_STATUS.EXPIRED
        || rawStatus === String(PARTIAL_OFFER_STATUS.EXPIRED)
        || Number(rawStatus) === PARTIAL_OFFER_STATUS.EXPIRED;
      setIsExpired(statusIsExpired || timeDiff <= 0);
    }
  }, [offerData]);

  const openResume = () => {
    if (!offerData.hygienist) return;
    if (showResume) {
      window.Cordova ?
        cordova?.InAppBrowser.open(
          offerData.hygienist?.resumeUrl,
          '_system',
          'location=yes'
        ) : window.open(offerData.hygienist?.resumeUrl);
    } else {
      setIsResumeModalOpen(true)
      return
    }
  }

  const showResume = offerData.hygienist?.resumeUrl && offerData.hygienist?.skills.length === 0;

  const renderRequestResubmitModal = () => {
    if (!offerData.job || offerId === null) {
      return null;
    }

    return (
      <RequestResubmitModal
        job={offerData.job}
        history={history}
        expiredOfferId={offerId!}      />
    );
  };

  useEffect(() => {
    if (offerData?.workExperience) {
      let latestWorkExperience = offerData.workExperience.find((experience) => experience.current );
      if (!latestWorkExperience) {
        latestWorkExperience = offerData.workExperience.sort((a, b) => {
          return dayjs(b.toDate).valueOf() - dayjs(a.toDate).valueOf();
        })[0]
      }
      setLatestWorkExperience(latestWorkExperience);
    }
  }, [offerData]);

  const professionalLicense = offerData.hygienist
    ? formatProfileHygienistName(offerData.hygienist, offerData.hygienist?.skills, { layout: 'stacked' })
    : '';

  const getShiftRoleLabel = (jobType?: number): string => {
    switch (jobType) {
      case Type.Admin:
        return 'Reception/Admin';
      case Type.Assistant:
        return 'Assistant';
      case Type.Hygienist:
      default:
        return 'Hygienist';
    }
  };

  const { formattedStartTime: formattedPartialStartTime, formattedEndTime: formattedPartialEndTime } = useMemo(() => {
    if ((process.env.NODE_ENV as string) === 'staging' || (process.env.NODE_ENV as string) === 'local') {
      debugTimeFormatting(
        offerData.partialOffer?.offeredStartTime,
        offerData.partialOffer?.offeredStartTimeLocal,
        'CustomOfferDetailsPage - Partial Times'
      );
    }

    // In instabook mode, show posted shift hours instead of partial
    if (instabookMode && offerData.job?.shifts?.[0]) {
      return formatPartialOfferTimeRange(
        offerData.job.shifts[0].postedStart,
        offerData.job.shifts[0].postedEnd,
        undefined,
        undefined,
        (offerData.job as any)?.location?.lat,
        (offerData.job as any)?.location?.lon,
      );
    }

    return formatPartialOfferTimeRange(
      offerData.partialOffer?.offeredStartTime,
      offerData.partialOffer?.offeredEndTime,
      offerData.partialOffer?.offeredStartTimeLocal,
      offerData.partialOffer?.offeredEndTimeLocal,
      offerData.partialOffer?.location?.lat,
      offerData.partialOffer?.location?.lon
    );
  }, [offerData.partialOffer, offerData.job, instabookMode]);

  const isPartialHours = useMemo(() => {
    if (!offerData.partialOffer || !offerData.job?.shifts?.[0]) {
      return false;
    }

    const jobShift = offerData.job.shifts[0];
    const { offeredStartTime, offeredEndTime } = offerData.partialOffer;

    const jobStartUTC = moment.utc(jobShift.originalPostedStart);
    const jobEndUTC = moment.utc(jobShift.originalPostedEnd);
    const offerStartUTC = moment.utc(offeredStartTime);
    const offerEndUTC = moment.utc(offeredEndTime);

    const format = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

    const isPartial = jobStartUTC.format(format) !== offerStartUTC.format(format) ||
                      jobEndUTC.format(format) !== offerEndUTC.format(format);

    return isPartial;
  }, [offerData.partialOffer, offerData.job]);

  const formatExpiryTime = () => {
    if (!offerData.partialOffer?.expiresAt) return '';
    
    const now = new Date();
    const expiresAt = new Date(offerData.partialOffer.expiresAt);
    const diffMs = expiresAt.getTime() - now.getTime();
    
    if (diffMs <= 0) return 'Expired';
    
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    return `Offer Expires in ${diffHrs}h ${diffMins}m`;
  };

  const renderHelpIcon = (tooltipText: string) => {
    const icon = (
      <InfoIcon
        className={styles.scoreHelpIcon}
        fontSize="small"
        onClick={(event) => {
          if (!isMobileTooltip()) return;
          event.preventDefault();
          event.stopPropagation();
          alert(tooltipText);
        }}
      />
    );

    return isMobileTooltip()
      ? icon
      : (
        <Tooltip
          title={tooltipText}
          arrow
          placement="top"
          classes={{ tooltip: styles.scoreTooltip }}
        >
          {icon}
        </Tooltip>
      );
  };

  const renderScoreRow = (
    label: string,
    valueLabel: string | null | undefined,
    percentile: number | null | undefined,
    fallback: string,
    tooltipText: string
  ) => {
    const scoreDisplay = getPerformanceScoreDisplay(valueLabel, percentile);

    return (
      <div className={styles.scoreRow}>
        {renderHelpIcon(tooltipText)}
        <span className={styles.scoreLabel}>{label}:</span>
        {scoreDisplay ? (
          <span className={`${styles.scoreValue} ${getScoreValueClass(scoreDisplay.label)}`}>
            {scoreDisplay.text}
          </span>
        ) : (
          <span className={styles.scoreFallback}>{fallback}</span>
        )}
      </div>
    );
  };

  const renderPerformanceScoreRows = () => {
    const membershipSummary = offerData.hygienist?.membershipSummary;

    return (
      <div className={styles.performanceScoreRows}>
        {renderScoreRow(
          'Reliability',
          membershipSummary?.reliabilityLabel,
          membershipSummary?.reliabilityPercentile,
          'Building reliability history',
          RELIABILITY_TOOLTIP
        )}
        {renderScoreRow(
          'Overall Feedback',
          membershipSummary?.feedbackLabel,
          membershipSummary?.feedbackPercentile,
          'Building feedback history',
          FEEDBACK_TOOLTIP
        )}
        {officeHistory && (
          <div className={`${styles.scoreRow} ${styles.officeHistoryRow}`} data-testid="offer-office-history">
            <span className={styles.scoreLabel}>History With Your Office:</span>
            <span className={styles.scoreFallback}>{getOfficeHistoryCopy(officeHistory)}</span>
          </div>
        )}
      </div>
    );
  };

  const renderRatings = () => {
    const ratingCount = offerData.hygienist?.numRatedJobs ?? 0;
    const rating = offerData.hygienist?.starScore ?? 0;

    return (
      <div className={styles.ratingContainer}>
        {renderPerformanceScoreRows()}
        <div className={styles.ratingStarsColumn}>
          {ratingCount === 0 ? (
            <span className={styles.ratingsCount}>No Rated Shifts yet</span>
          ) : (
            <>
              <div className={styles.stars}>
                {[1, 2, 3, 4, 5].map((star) =>
                  star <= Math.floor(rating) ? (
                    <StarIcon key={star} className={styles.star} />
                  ) : (
                    <StarBorderIcon key={star} className={styles.emptyStar} />
                  )
                )}
              </div>
              <span className={styles.ratingsCount}>
                {ratingCount} {ratingCount === 1 ? 'Rating' : 'Ratings'}
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  // Hourly rate: for instabook preview mode, show hygienist's autobook rate (T2-6432)
  const hourlyRate = instabookMode
    ? resolveInstabookHourlyRate(
        instabookHourlyRateFromState,
        offerData.hygienist as {
          hourlyRate?: number | string | null;
          GlobalSettings?: { hourlyRate?: number | string | null };
        } | null,
      )
    : `$${offerData?.partialOffer?.hourlyRate}/hr`;

  const shiftLocationSource = instabookMode
    ? (offerData.job as any)?.location
    : offerData.partialOffer?.location;
  const { line1: shiftLocationLine1, line2: shiftLocationLine2 } = formatShiftLocationLines(
    shiftLocationSource,
  );
  const offerDetailsReady = instabookMode
    || Boolean(offerData.partialOffer && offerData.hygienist);

  return (
    <>
    {loading ? <FixedLoader loading={loading || statsLoading} /> :
      !offerDetailsReady ? (
        <div className={styles.container}>
          <TopBarRedesigned title="Custom Offer Details" onBack={goBack} />
          {error && (
            <main className={styles.mainContent}>
              <div role="alert" style={{ color: '#B42318' }}>{error}</div>
            </main>
          )}
        </div>
      ) :
      <div className={styles.container}>
        <TopBarRedesigned
          title={`Dental ${getShiftRoleLabel(offerData.job?.type)} Shift`}
          onBack={goBack}
        />

        <Popup
          opened={isResumeModalOpen && !!offerData.hygienist}
          onClose={() => setIsResumeModalOpen(false)}
        >
          <TalentResume
            hygienist={offerData?.hygienist}
            onClose={() => setIsResumeModalOpen(false)}
          />
        </Popup>

        <div className={styles.mainContent}>
        <div className={styles.shiftDetailsCard}>
          <div className={styles.shiftHeadingRow}>
            <div className={styles.shiftDate}>
              <EventIcon className={styles.shiftDateIcon} />
              {shiftDateDisplay}
            </div>
          </div>
          
          <div className={styles.shiftCard}>
            <div className={styles.locationColumn}>
              <div className={styles.shiftLocation}>
                {shiftLocationLine1 ? (
                  <>
                    {shiftLocationLine1}
                    <br />
                  </>
                ) : null}
                {shiftLocationLine2}
              </div>
            </div>
            
            <div className={styles.timesColumn}>
              <div className={styles.shiftTimeGroup}>
                <span className={styles.shiftTimeLabel}>Start:</span>
                <span className={styles.shiftTimeValue}>
                  {formattedPartialStartTime}
                </span>
              </div>
              
              <div className={styles.shiftTimeGroup}>
                <span className={styles.shiftTimeLabel}>End:</span>
                <span className={styles.shiftTimeValue}>
                  {formattedPartialEndTime}
                </span>
              </div>
            </div>
          </div>

          {/* Partial Hours Offer Section */}
          {!instabookMode && isPartialHours && (
            <div className={styles.partialHoursContainer}>
              <div className={styles.partialHoursHeader}>
                <div className={styles.dashedCircle}></div>
                <span className={styles.partialHoursTitle}>Partial Hours Offer</span>
              </div>
              
              <div className={styles.hoursContent}>
                <div className={styles.hoursSection}>
                  <div className={styles.hoursLabel}>Hours Offered:</div>
                  <div className={styles.hoursValue}>
                    {formattedPartialStartTime} - {formattedPartialEndTime}
                  </div>
                </div>
                
                <div className={styles.hoursSection}>
                  <div className={styles.hoursLabel}>Original Shift Hours:</div>
                  <div className={styles.originalHoursValue}>
                    {formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedStart, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon)} - {formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedEnd, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon)}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={styles.detailsHeadingContainer}>
          <h2 className={styles.detailsHeading}>Custom Offer Details</h2>
        </div>

        <div className={styles.hygienistOfferCard}>
          <div className={styles.profileHeader}>
            <div className={styles.profileNameAndTitle}>
              {offerData.hygienist?.photoUrl && (
                <ProfileImage
                  photoUrl={offerData.hygienist.photoUrl}
                  className={styles.profileImage}
                />
              )}
              <div className={styles.profileInfo}>
                <h2 className={styles.profileName}>{professionalLicense}</h2>
                {offerData.hygienist?.graduationYear !== undefined && (
                <p className={styles.graduatedText}>
                  Graduated {offerData.hygienist?.graduationYear} from {offerData.hygienist?.school}
                </p>
                )}
                {formatFirstLicensedLabel(offerData.hygienist?.firstLicensedYear, offerData.hygienist?.firstLicensedMonth) && (
                  <p className={styles.graduatedText}>
                    {formatFirstLicensedLabel(offerData.hygienist?.firstLicensedYear, offerData.hygienist?.firstLicensedMonth)}
                  </p>
                )}
                {offerData.hygienist?.type === HYGIENIST_TYPES.HYGIENIST && (
                  <p className={styles.leftHanded}>
                    {offerData.hygienist?.firstName} is {offerData.hygienist?.isLeftHanded ? 'Left' : 'Right'} handed
                  </p>
                )}
              </div>
            </div>
            <div className={styles.hourlyRate}>
              <p className={styles.hourlyRateLabel}>Hourly Rate</p>
              <p className={styles.rate}>{ hourlyRate }</p>
            </div>
          </div>
          {renderRatings()}
          
          <p className={styles.offerExpiry}>{formatExpiryTime()}</p>

          <div className={styles.buttonsContainer}>
            {error && (
              <div role="alert" style={{ color: '#B42318', marginBottom: 8 }}>
                {error}
              </div>
            )}
            {instabookMode ? (
              <>
                <StyledButton
                  variant="contained"
                  color="primary"
                  size="small"
                  id="book-now-button"
                  className={styles.resumeButton}
                  onClick={handleInstabookBookNow}
                  disabled={officeUserLoading || bookingInstabook}
                >
                  {bookingInstabook ? 'Submitting…' : 'Book Now'}
                </StyledButton>
                <StyledButton
                  variant="outlined"
                  color="primary"
                  size="small"
                  id="continue-button"
                  className={styles.resumeButton}
                  onClick={openResume}
                  disabled={!offerData.hygienist}
                >
                  View Resume
                </StyledButton>
              </>
            ) : (
              !isExpired ? (
                <>
                  <AcceptOffer
                    offerData={{
                      isPartialHours: isPartialHours,
                      partialOffer: offerData.partialOffer!,
                      hygienist: offerData.hygienist!,
                      skills: offerData.hygienist?.skills || [],
                    }}
                    offerId={offerId!}
                    originalShiftHours={{
                      startTime: formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedStart, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon),
                      endTime: formatPartialOfferTime(offerData.job?.shifts?.[0]?.postedEnd, undefined, offerData.partialOffer?.location?.lat, offerData.partialOffer?.location?.lon)
                    }}
                    partialShiftHours={{
                      startTime: formattedPartialStartTime,
                      endTime: formattedPartialEndTime,
                    }}
                  />
                  <DeclineOffer
                    partialOffer={offerData.partialOffer!}
                    offerId={offerId as number}
                    key={offerId}
                  />
                  <StyledButton
                    variant="outlined"
                    color="primary"
                    size="small"
                    id="continue-button"
                    className={styles.resumeButton}
                    onClick={openResume}
                  >
                    View Resume
                  </StyledButton>
                </>
              ) : renderRequestResubmitModal()
            )}
          </div>

          {/* Display job-seeking status section if feature is active AND talent opted to share it */}
          {/* TODO (Remove Feature Flag): Remove 'offerData.partialOffer?.jobSeekingFeatureActive &&' check when 'jobSeekingStatus' flag is retired */}
          {offerData.partialOffer?.jobSeekingFeatureActive &&
           offerData.partialOffer?.showJobSeekingStatus === 1 &&
           offerData.hygienist &&
           offerData.hygienist.showForHire === 1 && (
            <div className={styles.jobSeekingStatusSection}>
              <p className={styles.jobSeekingStatusTitle}>{offerData.hygienist.firstName}'s Job-Seeking Status:</p>
              <div className={styles.jobSeekingStatusRow}>
                <WorkOutlineIcon className={styles.jobSeekingStatusIcon} />
                <p className={styles.jobSeekingStatusText}>
                  Actively Seeking Permanent Employment
                </p>
              </div>
            </div>
          )}

        </div>
        {/* Skills & Qualifications Section */}
        {offerData.hygienist?.CDHONumber && (
          <CollapsibleSection 
            title="Skills & Qualification (self-reported)" 
            icon={<AssignmentIcon />}
          >
            <p className={styles.licenseInfo}>License #: {offerData.hygienist.CDHONumber}</p>
            { languagesToShow?.length ? (
              <>
                <p className={styles.detailLabel}>Languages:</p>
                <p className={styles.detailValue}>
                  {languagesToShow.map((l) => l.name).join(', ')}
                </p>
              </>
            ) : null }
            <p className={styles.detailLabel}>Training & Certifications received:</p>
            {offerData.hygienist.skills?.length > 0 && offerData.hygienist.skills.map((skill, index) => (
              <div key={index} className={styles.skillItem}>
                <CheckCircleIcon className={styles.checkIcon} />
                <span>{skill.label}</span>
              </div>
            ))}
          </CollapsibleSection>
        )}

        {/* Work Experience Section */}
        {offerData.workExperience?.length > 0 && (
          <CollapsibleSection 
            title="Work Experience" 
            icon={<WorkIcon />}
          >
            {offerData.workExperience.map((experience, index) => {
              const jobTitle = 'jobTitle' in experience ? experience.jobTitle : '';
              const companyName = 'companyName' in experience ? experience.companyName : '';
              
              return (
                <div key={index} className={styles.workExperienceItem}>
                  <h4 className={styles.jobTitle}>
                    {jobTitle}
                  </h4>
                  <p className={styles.companyName}>
                    {companyName}
                  </p>
                  <p className={styles.jobPeriod}>
                    {experience.fromDate ? dayjs(experience.fromDate).format('MMMM YYYY') : 'January 2021'} - {
                      experience.current ? 
                        'Present' : 
                        (experience.toDate ? dayjs(experience.toDate).format('MMMM YYYY') : '')
                    }
                  </p>
                </div>
              );
            })}
          </CollapsibleSection>
        )}

        {/* About Me Section */}
        {offerData.hygienist?.about && (
          <CollapsibleSection 
            title="About Me" 
            icon={<InfoIcon />}
          >
            <p className={styles.aboutText}>{offerData.hygienist.about}</p>
          </CollapsibleSection>
        )}

        {/* Reminder Section */}
        <div className={styles.reminderSection}>
          <div className={styles.reminderHeader}>
            <WarningIcon className={styles.warningIcon} /> 
            <span className={styles.reminderTitle}>friendly reminder</span>
          </div>
          <p className={styles.reminderText}>
            Standard placement fees apply to shifts booked with {offerData.hygienist?.firstName} - 
            whether arranged on the app or through direct contact
          </p>
        </div>

        {/* Bottom Buttons */}
        <div className={styles.bottomButtons}>
          {offerData.partialOffer?.job && (
            <>
              <BookAnotherTemp job={offerData.job} />
              <CancelShiftButton job={offerData.job} />
            </>
          )}
        </div>

        <TimeDebugger
          timeData={{
            offeredStartTime: offerData.partialOffer?.offeredStartTime,
            offeredEndTime: offerData.partialOffer?.offeredEndTime,
            offeredStartTimeLocal: offerData.partialOffer?.offeredStartTimeLocal,
            offeredEndTimeLocal: offerData.partialOffer?.offeredEndTimeLocal,
            postedStartTime: offerData.job?.shifts?.[0]?.postedStart,
            postedEndTime: offerData.job?.shifts?.[0]?.postedEnd,
          }}
          location={{
            lat: offerData.partialOffer?.location?.lat,
            lon: offerData.partialOffer?.location?.lon,
          }}
        />
        </div>
      </div>
    }
      <PaymentRequiredModal
        isOpen={isPaymentModalOpen}
        locationId={paymentLocationId}
        jobId={paymentJobId || (location.state as any)?.job?.id || (location.state as any)?.partialOffer?.jobId}
        offerId={instabookMode ? null : offerId}
        requiresCardSetup={requiresCardSetup}
        isCardInvalid={isCardInvalid}
        paymentPolicy={paymentPolicy}
        corporateManaged={corporateManaged}
        paymentUnavailable={paymentUnavailable}
        checkpoint={instabookMode ? 'instabook_book_now' : 'offer_details_view'}
        sourcePage={instabookMode ? 'custom_offer_details' : offerDetailsSourcePage}
        onGoPaymentInfo={handleGoPaymentInfo}
        onClose={handleClosePaymentModal}
        onRetry={retryPaymentCheck}
      />
    </>
  );
};

export default CustomOfferDetailsPage;
